import { useTranslation } from "react-i18next";
import GenericCrudTable from "@/components/tables/genericCrudTable";
import RestrictedPage from "@/consumer/restrictedPage";
import { replaceDate } from "@/helper/dateHandling";
import { usePageHeading } from "@/helper/customHooks/usePageHeading";

export default function RuleList() {
  const { t } = useTranslation();
  usePageHeading(t("rules"));

  const selectOptions = [
    {
      value: "update_file",
      label: t("update_file"),
    },
    {
      value: "generate_pdf",
      label: t("generate_pdf"),
    },
    {
      value: "create_todos",
      label: t("create_todo"),
    },
    {
      value: "send_reminder_email_for_appointment_requests",
      label: t("send_reminder_email_for_appointment_requests"),
    },
    {
      value: "send_invitation_email_to_managers",
      label: t("send_invitation_email_to_managers"),
    },
    {
      value: "close_overdue_appointment_requests",
      label: t("close_overdue_appointment_requests"),
    },
    {
      value: "switch_entity_status_when_tasks_are_finished",
      label: t("switch_entity_status_when_tasks_are_finished"),
    },
    {
      value: "send_reminder_email_for_registration",
      label: t("send_reminder_email_for_registration"),
    },
    {
      value: "send_notification_email_for_new_files",
      label: t("send_notification_email_for_new_files"),
    },
    {
      value: "close_overdue_form_todos",
      label: t("close_overdue_form_todos"),
    },
    {
      value: "create_todo_from_additional_data",
      label: t("create_todo_from_additional_data"),
    },
    {
      value: "create_todo_from_gbu",
      label: t("create_todo_from_gbu"),
    },
    {
      value: "send_invitation_email_to_subject",
      label: t("send_invitation_email_to_subject"),
    },
    {
      value: "send_todo_notification_to_recently_registered_users",
      label: t("send_todo_notification_to_recently_registered_users"),
    },
    {
      value: "recalculate_calculated_anchor_data",
      label: t("recalculate_calculated_anchor_data"),
    },
    {
      value: "send_reminder_email_for_anamnese",
      label: t("send_reminder_email_for_anamnese"),
    },
    {
      value: "send_reminder_email_for_appointment",
      label: t("send_reminder_email_for_appointment"),
    },
    {
      value: "send_enrolment_notification_email_for_recent_enrolments",
      label: t("send_enrolment_notification_email_for_recent_enrolments"),
    },
    {
      value: "update_subject_order_index",
      label: t("update_subject_order_index"),
    },
    {
      value: "disable_outdated_appointment_slots",
      label: t("disable_outdated_appointment_slots"),
    },
    {
      value: "send_enrolment_reminder_emails",
      label: t("send_enrolment_reminder_emails"),
    },
    { value: "start_action_chain", label: t("start_action_chain") },
  ];

  return (
    <RestrictedPage permissionRequired="frontend_view_rule">
      <GenericCrudTable
        allowColumnFiltering={true}
        showColumnFilterbyDefault={true}
        entity="rule"
        columns={[
          { header: t("id"), accessorKey: "id", id: "id" },
          {
            header: t("name"),
            accessorKey: "name",
            id: "name",
            enableForCreate: true,
          },
          {
            header: t("classname"),
            accessorKey: "trigger_class_name",
            id: "trigger_class_name",
            enableForCreate: true,
          },
          {
            header: t("conditions"),
            accessorKey: "conditions",
            id: "conditions",
            enableSorting: false,
            customColumnFilterId: "conditions",
            customFilterParameter: "__icontains",
            required: true,
            errorMessage: t("field_required"),
            enableForCreate: true,
            accessorFn: (row: any) => {
              const conditions = [];
              if (row.conditions) {
                conditions.push(JSON.stringify(row.conditions));
              }

              return conditions.join(", ");
            },
            isJson: true,
          },
          {
            header: t("action"),
            accessorKey: "action",
            id: "action",
            required: true,
            filterVariant: "select",
            filterSelectOptions: selectOptions,
            errorMessage: t("field_required"),
            enableForCreate: true,
            createAble: true,
            selectOptions: selectOptions,
          },
          {
            header: t("active"),
            accessorKey: "disabled",
            id: "disabled",
            filterVariant: "select",
            filterSelectOptions: [
              { value: "true", label: t("no") },
              { value: "false", label: t("yes") },
            ],
            enableForCreate: true,
            selectOptions: [
              { value: true, label: t("no") },
              { value: false, label: t("yes") },
            ],
            accessorFn: (row: any) => {
              if (row.disabled) {
                return t("no");
              } else return t("yes");
            },
          },

          {
            header: t("scheduled"),
            accessorKey: "scheduled",
            id: "scheduled",
            enableForCreate: true,
            filterVariant: "select",
            filterSelectOptions: [
              { value: "true", label: t("yes") },
              { value: "false", label: t("no") },
            ],
            selectOptions: [
              { value: true, label: t("yes") },
              { value: false, label: t("no") },
            ],
            accessorFn: (row: any) => {
              if (row.scheduled) {
                return t("yes");
              } else return t("no");
            },
          },
          {
            header: t("additional_data"),
            accessorKey: "additional_data",
            customColumnFilterId: "additional_data",
            customFilterParameter: "__icontains",
            id: "additional_data",
            enableSorting: false,
            required: true,
            errorMessage: t("field_required"),
            enableForCreate: true,
            createDefaultValue: "{}",
            accessorFn: (row: any) => {
              const additionalData = [];
              if (row.additional_data) {
                additionalData.push(JSON.stringify(row.additional_data));
              }

              return additionalData.join(", ");
            },
            isJson: true,
          },
          {
            header: t("created_on"),
            accessorKey: "created_on",
            id: "created_on",
            filterVariant: "date",
            sortingFn: "datetime",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("created_by"),
            accessorKey: "created_by",
            customColumnFilterId: "created_by_name",

            id: "created_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
          {
            header: t("modified_on"),
            accessorKey: "modified_on",
            filterVariant: "date",

            id: "modified_on",
            sortingFn: "datetime",
            Cell: ({ cell }: any) => {
              return <span>{replaceDate(cell.getValue(), true, true)}</span>;
            },
          },
          {
            header: t("modified_by"),
            accessorKey: "modified_by",
            customColumnFilterId: "modified_by_name",
            id: "modified_by",
            Cell: ({ cell }: any) => {
              return <span>{cell.getValue()?.name}</span>;
            },
          },
        ]}
      />
    </RestrictedPage>
  );
}
